import { useEffect, useMemo, useState } from 'react'
import { Download, Loader2, RefreshCw } from 'lucide-react'
import api from '../../utils/api'
import { exportRowsToExcel } from '../../utils/exportExcel'

function formatAmount(value) {
  const amount = Number(value || 0)
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
}

function formatPercent(value) {
  const percent = Number(value || 0)
  return `${Number.isFinite(percent) ? percent.toFixed(2) : '0.00'}%`
}

export default function ChannelReportSummaryTab() {
  const [loading, setLoading] = useState(true)
  const [latestDate, setLatestDate] = useState('')
  const [summary, setSummary] = useState({
    total_user_count: 0,
    total_recharge_amount: 0,
    total_paid_user_count: 0,
    total_payment_rate: 0,
    total_avg_paid_amount: 0
  })
  const [list, setList] = useState([])

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await api.get('/admin/reporting-reports/channel-summary')
      if (response.data.success) {
        const data = response.data.data || response.data || {}
        setLatestDate(data.latest_date || '')
        setSummary(data.summary || {})
        setList(Array.isArray(data.list) ? data.list : [])
      }
    } catch (error) {
      console.error('获取渠道统计失败:', error)
      setList([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const displayRows = useMemo(() => {
    return [...list].sort((a, b) => {
      const rechargeCompare = Number(b.recharge_amount || 0) - Number(a.recharge_amount || 0)
      if (rechargeCompare !== 0) return rechargeCompare

      const paidCompare = Number(b.paid_user_count || 0) - Number(a.paid_user_count || 0)
      if (paidCompare !== 0) return paidCompare

      const userCompare = Number(b.user_count || 0) - Number(a.user_count || 0)
      if (userCompare !== 0) return userCompare

      return String(a.channel_name || '').localeCompare(String(b.channel_name || ''))
    })
  }, [list])

  const handleExport = () => {
    if (displayRows.length === 0) {
      return
    }

    exportRowsToExcel({
      fileName: `渠道统计_${latestDate || 'latest'}`,
      sheetName: '渠道统计',
      columns: [
        { header: '渠道', key: 'channel_name', width: 18 },
        { header: '用户数', key: 'user_count', width: 10 },
        { header: '充值金额', value: (row) => formatAmount(row.recharge_amount), width: 14 },
        { header: '付费用户', key: 'paid_user_count', width: 12 },
        { header: '付费率', value: (row) => formatPercent(row.payment_rate), width: 12 },
        { header: '充值用户人均付费金额', value: (row) => formatAmount(row.average_paid_amount), width: 20 }
      ],
      rows: [
        ...displayRows,
        {
          channel_name: '合计',
          user_count: summary.total_user_count || 0,
          recharge_amount: summary.total_recharge_amount || 0,
          paid_user_count: summary.total_paid_user_count || 0,
          payment_rate: summary.total_payment_rate || 0,
          average_paid_amount: summary.total_avg_paid_amount || 0
        }
      ]
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">渠道统计</h2>
          <p className="mt-1 text-sm text-gray-500">展示最新统计日期截至当前的各渠道累计用户、充值与付费数据</p>
        </div>
        <button
          type="button"
          onClick={fetchData}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" />
          刷新
        </button>
        <button
          type="button"
          onClick={handleExport}
          disabled={loading || displayRows.length === 0}
          className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
        >
          <Download className="h-4 w-4" />
          导出 Excel
        </button>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-600 shadow-sm">
        截至：{latestDate || '--'}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">渠道</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">用户数</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">充值金额</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">付费用户</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">付费率</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">充值用户人均付费金额</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-600" />
                  </td>
                </tr>
              ) : displayRows.length > 0 ? (
                <>
                  {displayRows.map((item) => (
                    <tr key={item.channel_name} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{item.channel_name}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-900">{item.user_count || 0}</td>
                      <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">{formatAmount(item.recharge_amount)}</td>
                      <td className="px-6 py-4 text-right text-sm font-medium text-blue-600">{item.paid_user_count || 0}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-900">{formatPercent(item.payment_rate)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-900">{formatAmount(item.average_paid_amount)}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">合计</td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">{summary.total_user_count || 0}</td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">{formatAmount(summary.total_recharge_amount)}</td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">{summary.total_paid_user_count || 0}</td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">{formatPercent(summary.total_payment_rate)}</td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">{formatAmount(summary.total_avg_paid_amount)}</td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                    暂无数据
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
